import type { LatLngPoint } from 'components/search-es/types';

/**
 * Where the user left the Area Page V2 map when a language change reloaded it.
 *
 * The map's live position lives only in React state, so any full page load
 * drops it and the map reopens at the area's optimal zoom. That is what the
 * locale switcher does — `LocaleManager.switchToLocale` navigates with
 * `window.location.href` — so changing the language on eXp CA threw the user
 * back out to the whole city even though the page, the area and the filters
 * were unchanged.
 *
 * Deliberately narrow: only a locale switch's own unload records a position
 * (see `pagehide` in `useAreaPageV2Workspace`, gated on
 * `isLocaleSwitchNavigation`). A refresh, and every other way of arriving,
 * records nothing and so restores nothing — those still re-frame on the area,
 * which is what a reload of an area page is expected to do. Changing the
 * language is not a request to look somewhere else, so it alone is carried
 * over.
 *
 * The viewport is not put in the URL on purpose: `getTargetedUrl` strips
 * latitude/longitude/zoom from area-page URLs to keep them canonical, so the
 * address bar is not a place this can be stored. `sessionStorage` scopes it to
 * the one tab, so a shared link opened elsewhere is unaffected.
 */
const STORAGE_KEY = 'areaPageV2.mapViewport';

/**
 * A recorded position is meant to be picked up by the load the locale switch
 * immediately caused, so anything older is a leftover and is ignored.
 */
const MAX_AGE_MS = 10 * 60 * 1000;

export type StoredMapViewport = {
  /** The area this position was recorded for; a different area must not reuse it. */
  slug: string;
  center: LatLngPoint;
  zoom: number;
};

type StoredPayload = StoredMapViewport & { savedAt: number };

function isUsableNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function parsePayload(raw: string): StoredPayload | null {
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { slug, center, zoom, savedAt } = parsed as Partial<StoredPayload>;
  if (typeof slug !== 'string' || !slug) return null;
  if (!isUsableNumber(zoom) || zoom <= 0) return null;
  if (!isUsableNumber(savedAt)) return null;
  if (typeof center !== 'object' || center === null) return null;
  if (!isUsableNumber(center.lat) || !isUsableNumber(center.lng)) return null;
  return { slug, center: { lat: center.lat, lng: center.lng }, zoom, savedAt };
}

/**
 * The position recorded for `slug`, or null when there is none, it belongs to
 * another area, or it has gone stale.
 *
 * Read-only, and safe to call more than once for the same load — callers read
 * during render, which React may repeat. {@link clearStoredMapViewport} is what
 * ends the handover, so one locale switch feeds exactly one load and a refresh
 * of the page it restored on gets the area's own framing again.
 *
 * Storage access is wrapped because `sessionStorage` throws outright when site
 * data is blocked, and hands back whatever a previous release wrote.
 */
export function readStoredMapViewport(slug: string): StoredMapViewport | null {
  if (typeof window === 'undefined' || !slug) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const payload = parsePayload(raw);
    if (!payload || payload.slug !== slug) return null;
    if (Date.now() - payload.savedAt > MAX_AGE_MS) return null;
    return { slug: payload.slug, center: payload.center, zoom: payload.zoom };
  } catch {
    return null;
  }
}

/**
 * Ends the handover, whether or not this page could use it — a position left
 * for an area the user is no longer on must not resurface later in the tab.
 */
export function clearStoredMapViewport(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Blocked storage had nothing to hand over in the first place.
  }
}

export function writeStoredMapViewport({ slug, center, zoom }: StoredMapViewport): void {
  if (typeof window === 'undefined' || !slug) return;
  if (!isUsableNumber(zoom) || zoom <= 0) return;
  if (!isUsableNumber(center.lat) || !isUsableNumber(center.lng)) return;
  try {
    const payload: StoredPayload = { slug, center, zoom, savedAt: Date.now() };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage is full or blocked — the map just opens at the area's own zoom.
  }
}
